import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Cookie, Lock, Shield, Sparkles } from "lucide-react";

const COOKIE_PREFERENCES_KEY = "synergy_cookie_preferences";

const defaultCookiePreferences = {
  essential: true,
  analytics: true,
  marketing: false,
  personalization: true,
};

function LegalPageFrame({ accent, badgeIcon, badge, title, intro, meta, cta, sections, aside, children }) {
  return (
    <div className="relative overflow-hidden bg-[#050109] text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(94,48,195,0.28),transparent_34%),radial-gradient(circle_at_85%_18%,rgba(65,28,134,0.34),transparent_24%),linear-gradient(180deg,#0a0615_0%,#050109_38%,#050109_100%)]" />
      <div className="pointer-events-none absolute left-[-8rem] top-32 h-72 w-72 rounded-full bg-[#8B5CF6]/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[-5rem] top-56 h-80 w-80 rounded-full bg-[#4F46E5]/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(18,12,34,0.92),rgba(11,8,24,0.9))] shadow-[0_35px_120px_rgba(8,6,28,0.55)]">
          <div className="grid gap-10 px-6 py-8 sm:px-10 sm:py-10 lg:grid-cols-[minmax(0,1.45fr)_320px] lg:px-12 lg:py-12">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/[0.74]">
                {badgeIcon}
                <span>{badge}</span>
              </div>
              <h1 className="mt-6 text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl lg:text-[4.25rem]">
                <span className={`bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>
                  {title}
                </span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-white/[0.72] sm:text-lg">
                {intro}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                {meta.map((item) => (
                  <div
                    key={item}
                    className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white/[0.74]"
                  >
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to={cta.primaryTo}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#A855F7] via-[#8B5CF6] to-[#6366F1] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(119,74,255,0.38)] transition hover:scale-[1.02]"
                >
                  {cta.primaryLabel}
                  <ChevronRight size={16} />
                </Link>
                <Link
                  to={cta.secondaryTo}
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-6 py-3 text-sm font-semibold text-white/[0.86] transition hover:bg-white/[0.08]"
                >
                  {cta.secondaryLabel}
                </Link>
              </div>
            </div>

            <aside className="relative">
              <div className="h-full rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.02))] p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/[0.55]">
                  Quick Summary
                </p>
                <div className="mt-5 space-y-3">
                  {aside.map((item) => (
                    <div
                      key={item.title}
                      className="rounded-2xl border border-white/8 bg-black/20 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-xl bg-white/[0.08] p-2 text-white">
                          {item.icon}
                        </div>
                        <div>
                          <h2 className="text-sm font-semibold text-white">{item.title}</h2>
                          <p className="mt-1 text-sm leading-6 text-white/[0.66]">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="space-y-6">
            {sections.map((section, index) => (
              <section
                key={section.title}
                className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.025))] p-6 shadow-[0_20px_50px_rgba(6,4,18,0.3)] transition hover:border-white/15 hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] sm:p-8"
              >
                <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 rounded-full bg-[#8B5CF6]/10 blur-2xl transition group-hover:scale-125" />
                <div className="relative flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-sm font-bold text-white/[0.88]">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-2xl font-semibold tracking-tight text-white">{section.title}</h2>
                    {section.lead ? (
                      <p className="mt-2 text-sm leading-7 text-white/[0.66] sm:text-base">{section.lead}</p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-6 space-y-4 text-sm leading-7 text-white/[0.74] sm:text-base">
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>

                {section.bullets?.length ? (
                  <div className="mt-6 grid gap-3">
                    {section.bullets.map((bullet) => (
                      <div
                        key={bullet}
                        className="flex items-start gap-3 rounded-2xl border border-white/[0.08] bg-black/20 px-4 py-4"
                      >
                        <div className="mt-0.5 rounded-full bg-emerald-400/12 p-1 text-emerald-300">
                          <Check size={14} />
                        </div>
                        <p className="text-sm leading-6 text-white/[0.76]">{bullet}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}

            {children}
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/[0.5]">
                On This Page
              </p>
              <div className="mt-4 space-y-2">
                {sections.map((section, index) => (
                  <div
                    key={section.title}
                    className="rounded-2xl border border-white/[0.08] bg-black/20 px-4 py-3 text-sm text-white/[0.72]"
                  >
                    <span className="mr-2 text-white/[0.45]">{String(index + 1).padStart(2, "0")}</span>
                    {section.title}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function CookiePreferenceCard({ title, description, enabled, locked, onToggle }) {
  return (
    <section className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.025))] p-6 sm:p-8">
      <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 rounded-full bg-[#8B5CF6]/10 blur-2xl" />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold text-white">{title}</h2>
          <p className="mt-3 text-sm leading-7 text-white/[0.72] sm:text-base">{description}</p>
        </div>

        <button
          type="button"
          onClick={onToggle}
          disabled={locked}
          className={`inline-flex min-w-[150px] items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition ${
            enabled
              ? "bg-gradient-to-r from-[#A855F7] to-[#6366F1] text-white shadow-[0_16px_35px_rgba(109,69,255,0.34)]"
              : "border border-white/15 bg-white/[0.04] text-white/[0.75] hover:bg-white/[0.08]"
          } ${locked ? "cursor-not-allowed opacity-90" : ""}`}
        >
          {locked ? "Always active" : enabled ? "Enabled" : "Disabled"}
        </button>
      </div>
    </section>
  );
}

export function PrivacyPolicyPage() {
  const sections = [
    {
      title: "Information We Collect",
      lead: "We only collect data needed to operate the product, support users, and secure transactions.",
      paragraphs: [
        "Synergy Coin may collect account details such as your name, email address, phone number, referral data, billing details, transaction history, wallet-related information, and technical signals like browser type, IP address, cookie identifiers, and device data.",
      ],
      bullets: [
        "Account data submitted during signup, login, profile updates, or support requests.",
        "Purchase and payment records needed to confirm packages, bonuses, and receipts.",
        "Usage analytics that help us understand traffic, performance, and security issues.",
      ],
    },
    {
      title: "How We Use Information",
      paragraphs: [
        "Synergy Coin uses collected data to deliver platform features, verify purchases, administer referral rewards, send account notifications, respond to support requests, improve product performance, and detect fraud or misuse.",
        "We may also use contact details for operational notices, policy updates, onboarding flows, and promotional communication where permitted by law or where you have given consent.",
      ],
    },
    {
      title: "Sharing and Retention",
      paragraphs: [
        "Information may be shared with payment processors, hosting providers, analytics vendors, customer support tools, or legal authorities when necessary to operate the service, enforce our terms, or comply with lawful requests.",
        "We keep data only for as long as reasonably necessary for business, legal, accounting, and security purposes. When retention is no longer required, we delete or anonymize it where practical.",
      ],
    },
    {
      title: "Your Choices",
      paragraphs: [
        "You may request access, correction, or deletion of certain personal data, subject to verification and applicable law. You can also manage cookies through the cookie settings page and opt out of non-essential marketing communication using the unsubscribe options we provide.",
      ],
    },
  ];

  return (
    <LegalPageFrame
      accent="from-white via-[#E9D5FF] to-[#A78BFA]"
      badgeIcon={<Shield size={14} />}
      badge="Privacy"
      title="Privacy Policy"
      intro="This page explains what information Synergy Coin collects, why we collect it, and how it is used to operate the website, process registrations, and support purchases."
      meta={["Effective date: April 15, 2026", "Applies to website and account services", "Cookie preferences supported"]}
      cta={{
        primaryLabel: "Manage cookies",
        primaryTo: "/cookie-settings",
        secondaryLabel: "Back to home",
        secondaryTo: "/",
      }}
      aside={[
        {
          icon: <Shield size={16} />,
          title: "Data scope",
          description: "Covers account details, transaction records, referral activity, and technical usage data.",
        },
        {
          icon: <Lock size={16} />,
          title: "Control",
          description: "Users can request corrections or deletion where applicable and manage non-essential cookies.",
        },
        {
          icon: <Cookie size={16} />,
          title: "Retention",
          description: "Information is kept only as long as operational, legal, or security needs require.",
        },
      ]}
      sections={sections}
    />
  );
}

export function TermsOfServicePage() {
  const sections = [
    {
      title: "Eligibility and Accounts",
      lead: "Using the platform means you are responsible for the account and the information attached to it.",
      paragraphs: [
        "By using the service, you confirm that the information you provide is accurate and that you are legally permitted to access the platform in your jurisdiction.",
        "You are responsible for maintaining the confidentiality of your account credentials and for all activity occurring under your account.",
      ],
    },
    {
      title: "Platform Use",
      paragraphs: [
        "We may suspend or terminate access if we believe your conduct creates legal, financial, reputational, or security risk to Synergy Coin or its users.",
      ],
      bullets: [
        "Do not misuse the website, interfere with security, or attempt unauthorized access.",
        "Do not upload unlawful, deceptive, harmful, or infringing content.",
        "Do not misrepresent referral, payment, or investment outcomes to other users.",
      ],
    },
    {
      title: "Payments, Presale, and Risk",
      paragraphs: [
        "Any purchase, presale participation, or token-related access shown on the platform is subject to availability, verification, pricing updates, and applicable compliance checks.",
        "Nothing on the site should be treated as financial, tax, or investment advice. Digital asset activity can involve substantial risk, including volatility, regulation, and technical failure.",
      ],
    },
    {
      title: "Disclaimers and Liability Limits",
      paragraphs: [
        "The service is provided on an \"as is\" and \"as available\" basis to the extent permitted by law. We do not guarantee uninterrupted availability, error-free operation, or future token value, listing, or utility.",
        "To the fullest extent allowed by law, Synergy Coin will not be liable for indirect, incidental, consequential, or special damages arising from your use of the service.",
      ],
    },
  ];

  return (
    <LegalPageFrame
      accent="from-white via-[#F5D0FE] to-[#C084FC]"
      badgeIcon={<Sparkles size={14} />}
      badge="Legal"
      title="Terms of Service"
      intro="These terms govern access to the Synergy Coin website, account features, referral tools, and presale-related services."
      meta={["Draft legal copy", "Platform, account, and payment use", "Legal review recommended before launch"]}
      cta={{
        primaryLabel: "Privacy policy",
        primaryTo: "/privacy-policy",
        secondaryLabel: "Back to home",
        secondaryTo: "/",
      }}
      aside={[
        {
          icon: <Shield size={16} />,
          title: "User duties",
          description: "Provide accurate information, protect credentials, and follow platform restrictions.",
        },
        {
          icon: <Lock size={16} />,
          title: "Risk notice",
          description: "Presale and token-related access remain subject to availability, review, and jurisdictional limits.",
        },
        {
          icon: <Sparkles size={16} />,
          title: "Liability",
          description: "Service is provided as available and does not guarantee token value or uninterrupted uptime.",
        },
      ]}
      sections={sections}
    />
  );
}

export function CookieSettingsPage() {
  const [preferences, setPreferences] = useState(defaultCookiePreferences);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const stored = window.localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (!stored) {
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      setPreferences((previous) => ({ ...previous, ...parsed, essential: true }));
    } catch (_error) {
      window.localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    }
  }, []);

  const cookieRows = useMemo(
    () => [
      {
        key: "essential",
        label: "Essential cookies",
        description: "Required for security, login, checkout flow, and core site functionality.",
        locked: true,
      },
      {
        key: "analytics",
        label: "Analytics cookies",
        description: "Help us measure traffic, performance, and product usage patterns.",
      },
      {
        key: "marketing",
        label: "Marketing cookies",
        description: "Support promotions, ad measurement, and campaign attribution.",
      },
      {
        key: "personalization",
        label: "Personalization cookies",
        description: "Remember interface choices and improve the experience across visits.",
      },
    ],
    [],
  );

  const updatePreference = (key) => {
    setSaved(false);
    setPreferences((previous) => ({
      ...previous,
      [key]: key === "essential" ? true : !previous[key],
    }));
  };

  const savePreferences = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    }
    setSaved(true);
  };

  const sections = [
    {
      title: "How These Settings Work",
      lead: "These controls are stored locally in your browser for this device.",
      paragraphs: [
        "If you clear site storage, switch browsers, or move to another device, you may need to set them again.",
        "Some third-party tools can also be controlled through browser privacy settings, content blockers, or operating system preferences.",
      ],
    },
  ];

  return (
    <LegalPageFrame
      accent="from-white via-[#BFDBFE] to-[#60A5FA]"
      badgeIcon={<Cookie size={14} />}
      badge="Preferences"
      title="Cookie Settings"
      intro="Control which non-essential cookies Synergy Coin can use on your device. Essential cookies stay active because the site cannot function correctly without them."
      meta={["Saved in your browser", "Essential cookies stay on", "Non-essential choices are editable"]}
      cta={{
        primaryLabel: "Save preferences",
        primaryTo: "/cookie-settings",
        secondaryLabel: "Back to home",
        secondaryTo: "/",
      }}
      aside={[
        {
          icon: <Cookie size={16} />,
          title: "Essential",
          description: "Required for the site to work correctly and cannot be switched off here.",
        },
        {
          icon: <Shield size={16} />,
          title: "Privacy control",
          description: "Analytics, marketing, and personalization can be changed at any time.",
        },
        {
          icon: <Lock size={16} />,
          title: "Local storage",
          description: "Preferences are stored on this browser only until you clear site data.",
        },
      ]}
      sections={sections}
    >
      <section className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.025))] p-6 sm:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/[0.5]">
              Controls
            </p>
            <h2 className="mt-3 text-3xl font-semibold text-white">Choose your cookie preferences</h2>
          </div>
          <p className="max-w-xl text-sm leading-7 text-white/[0.68]">
            Essential cookies remain active. The rest can be toggled individually and saved below.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {cookieRows.map((row) => (
            <CookiePreferenceCard
              key={row.key}
              title={row.label}
              description={row.description}
              enabled={preferences[row.key]}
              locked={row.locked}
              onToggle={() => updatePreference(row.key)}
            />
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={savePreferences}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#A855F7] to-[#6366F1] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(119,74,255,0.38)] transition hover:scale-[1.02]"
          >
            Save cookie settings
            <Check size={16} />
          </button>
          {saved ? (
            <p className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm text-emerald-300">
              Your preferences have been saved.
            </p>
          ) : null}
        </div>
      </section>
    </LegalPageFrame>
  );
}
